"use client";

import Image from "next/image";
import { cn } from "@/lib/utils";

interface LogoProps {
  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Whether to show the text alongside the icon */
  showText?: boolean;
  /** Custom className for the container */
  className?: string;
  /** Custom className for the icon */
  iconClassName?: string;
  /** Custom className for the text */
  textClassName?: string;
  /** Text to display (defaults to the brand name) */
  text?: string;
}

const sizeConfig = {
  xs: { icon: "h-6 w-6", iconPx: 24, text: "text-sm", gap: "gap-1.5" },
  sm: { icon: "h-8 w-8", iconPx: 32, text: "text-base", gap: "gap-2" },
  md: { icon: "h-10 w-10", iconPx: 40, text: "text-lg", gap: "gap-2.5" },
  lg: { icon: "h-12 w-12", iconPx: 48, text: "text-xl", gap: "gap-3" },
  xl: { icon: "h-14 w-14", iconPx: 56, text: "text-2xl", gap: "gap-3" },
};

/**
 * Wrenk logo lockup — the mark plus the wordmark.
 *
 * The mark is a pair of leaning wrench heads meeting over a descending stem:
 * the tool that reaches into a customer's back office and turns something.
 * Its single path lives in `@/lib/config/brand-mark`; the raster tiles this
 * component renders come from `scripts/brand/generate-assets.mjs`.
 */
export function Logo({
  size = "md",
  showText = true,
  className,
  iconClassName,
  textClassName,
  text = "Wrenk",
}: LogoProps) {
  const config = sizeConfig[size];

  return (
    <div className={cn("flex items-center", config.gap, className)}>
      <LogoIcon className={cn(config.icon, iconClassName)} />
      {showText && (
        <span
          className={cn(
            "font-semibold font-[Manrope,ui-sans-serif,system-ui]",
            config.text,
            "text-foreground",
            textClassName
          )}
        >
          {text}
        </span>
      )}
    </div>
  );
}

interface LogoIconProps {
  className?: string;
  /** Tile variant for app icons */
  tile?: "none" | "dark" | "blue" | "light";
}

/**
 * The Wrenk mark on its own, as a pre-rendered tile.
 * PNG rather than inline SVG so it rasterises identically in every browser and
 * in the Next edge `ImageResponse` used for the favicon and apple-touch icon.
 */
export function LogoIcon({ className, tile = "none" }: LogoIconProps) {
  // Select the appropriate PNG based on tile variant
  // app-icon-dark.png is the B icon on dark rounded square background
  // app-icon-light.png is the B icon on light rounded square background
  const imageSrc = tile === "dark" || tile === "blue"
    ? "/logos/app-icon-dark.png"
    : tile === "light"
    ? "/logos/app-icon-light.png"
    : "/logos/app-icon-dark.png"; // default to dark icon

  return (
    <Image
      src={imageSrc}
      alt="Wrenk logo"
      width={40}
      height={40}
      className={cn("flex-shrink-0 object-contain", className)}
    />
  );
}

/**
 * Minimal logo badge for compact spaces (headers, nav items)
 */
export function LogoBadge({
  className,
  variant = "dark",
}: {
  className?: string;
  variant?: "dark" | "blue" | "light";
}) {
  return (
    <div className={cn("grid place-items-center", className)}>
      <LogoIcon className="h-full w-full" tile={variant} />
    </div>
  );
}

// ============================================================================
// Feature/Module Icons - Using PNG images
// ============================================================================

export type FeatureIconType = "health" | "data" | "deploy" | "ai";

interface FeatureIconProps {
  feature: FeatureIconType;
  className?: string;
}

const featureIconAlt: Record<FeatureIconType, string> = {
  health: "Health checks",
  data: "Data management",
  deploy: "CI/CD automation",
  ai: "AI consultant",
};

const featureIconSrc: Record<FeatureIconType, string> = {
  health: "/logos/feature-health.png",
  data: "/logos/feature-data.png",
  deploy: "/logos/feature-deploy.png",
  ai: "/logos/feature-ai.png",
};

/**
 * Module icons for the four main Wrenk modules, in the same language as the
 * Wrenk mark: flat navy fills with one mint accent, square on a 64 grid.
 * - Health: shield with a check
 * - Data: stacked slabs, the live layer on top
 * - Deploy: three chevrons, the last one shipped
 * - AI: a four-point spark
 *
 * They are square because every consumer renders them in a square box
 * (h-5 through h-10); they are navy-on-light because every consumer renders
 * them on white or a pale tint. Regenerate with
 * `scripts/brand/generate-feature-icons.mjs`, never by hand.
 */
export function FeatureIcon({ feature, className }: FeatureIconProps) {
  return (
    <Image
      src={featureIconSrc[feature]}
      alt={featureIconAlt[feature]}
      width={32}
      height={32}
      className={cn("flex-shrink-0 object-contain", className)}
    />
  );
}

interface FeatureIconComponentProps {
  className?: string;
}

function HealthIcon({ className }: FeatureIconComponentProps) {
  return (
    <Image
      src="/logos/feature-health.png"
      alt="Health checks"
      width={32}
      height={32}
      className={cn("flex-shrink-0 object-contain", className)}
    />
  );
}

function DataIcon({ className }: FeatureIconComponentProps) {
  return (
    <Image
      src="/logos/feature-data.png"
      alt="Data management"
      width={32}
      height={32}
      className={cn("flex-shrink-0 object-contain", className)}
    />
  );
}

function DeployIcon({ className }: FeatureIconComponentProps) {
  return (
    <Image
      src="/logos/feature-deploy.png"
      alt="CI/CD automation"
      width={32}
      height={32}
      className={cn("flex-shrink-0 object-contain", className)}
    />
  );
}

function AIIcon({ className }: FeatureIconComponentProps) {
  return (
    <Image
      src="/logos/feature-ai.png"
      alt="AI consultant"
      width={32}
      height={32}
      className={cn("flex-shrink-0 object-contain", className)}
    />
  );
}

// Export individual icons for direct use
export { HealthIcon, DataIcon, DeployIcon, AIIcon };
